// Reintentar / Reimprimir for NATIVE job records (desktop's live in the
// bridge strategy — see V2PrintingJobsTab + resubmitJob). Gating is the
// desktop matrix verbatim:
// - Retry: failed + retrySafety 'safe' (provably nothing printed) + bytes
//   still on the record + NOT an auto-print (catch-up owns those — a raw
//   resend would skip the printed counters and double-print later).
// - Reprint: any terminal record with bytes; 'unknown' or failed-non-safe
//   needs the confirm dialog (the document MAY have printed).
// Resubmissions target the destination's CURRENT assignment (the user may
// have reassigned after the failure), falling back to the job's original
// printer by name, and create a NEW job record through addPrintJob.
import { computed, ref } from 'vue';
import {
  IPrintContext,
  usePrinting,
  usePrintingStore,
} from '@/composition/use-printing';
import { IPrinter } from '@/interfaces/IPrinter';
import { IPrintJob } from '@/interfaces/printing.interfaces';
import { PrinterType } from '@/modules/PrintService';
import { jobStatusOfError, retrySafetyOf } from '@/lib/print/retry-safety';

/**
 * MODULE scope on purpose (same rationale as testingPrinterId in
 * use-native-print-tests.ts): the double-submit guard must hold across every
 * surface that renders job actions, not per composable instance.
 */
const busyJobIds = ref<string[]>([]);

export function useNativeJobActions() {
  const printing = usePrinting();
  const store = usePrintingStore();

  function isTerminal(job: IPrintJob): boolean {
    // Native records are terminal by construction (created after the
    // strategy settles) — the helper exists for desktop-parity readability.
    return (
      job.status === 'printed' ||
      job.status === 'failed' ||
      job.status === 'unknown'
    );
  }

  function canRetry(job: IPrintJob): boolean {
    return (
      job.status === 'failed' &&
      job.retrySafety === 'safe' &&
      !!job.data?.length &&
      !job.isAutoPrint
    );
  }

  function canReprint(job: IPrintJob): boolean {
    return isTerminal(job) && !!job.data?.length;
  }

  /** Reprint of a possibly-printed document needs a human check first. */
  function needsReprintConfirm(job: IPrintJob): boolean {
    if (job.status === 'unknown') return true;
    return job.status === 'failed' && job.retrySafety !== 'safe';
  }

  /**
   * Current printer for the job's destination (live assignment), falling
   * back to the job's original printer resolved by name.
   */
  function resubmitTargetOf(job: IPrintJob): IPrinter | null {
    let assigned: IPrinter | null = null;
    if (job.printerType === PrinterType.Ticket) {
      assigned = printing.ticketPrinter.value;
    } else if (job.printerType === PrinterType.Etiqueta) {
      assigned = printing.stickerPrinter.value;
    } else if (job.comandaStationId !== undefined) {
      assigned =
        printing.comandaPrintersByStation.value[job.comandaStationId] ?? null;
    }
    if (assigned) return assigned;

    if (!job.printerName) return null;
    return (
      (printing.printers.value ?? []).find(
        (p) => p.name === job.printerName
      ) ?? null
    );
  }

  /**
   * Resubmits the job's bytes as a NEW manual job. Throws on failure (the
   * caller owns toasts); the new record (printed/failed/unknown) is added
   * here either way.
   */
  async function resubmit(job: IPrintJob): Promise<void> {
    if (!job.data?.length) throw new Error('El trabajo no tiene datos');
    const printer = resubmitTargetOf(job);
    if (!printer) throw new Error('No hay impresora disponible');
    if (busyJobIds.value.includes(job.jobId)) return;

    const printContext: IPrintContext = {
      kind:
        job.printerType === PrinterType.Etiqueta
          ? 'sticker'
          : job.printerType === PrinterType.Comanda
          ? 'comanda'
          : 'ticket',
      label: job.title,
      stationId: job.comandaStationId,
      // No dedupeKey: manual resubmissions must never collapse into an
      // auto-print's dedupe window.
    };

    busyJobIds.value = [...busyJobIds.value, job.jobId];
    try {
      await printing.printByteArray(printer, job.data, printContext);
      store.addPrintJob({
        printerType: job.printerType,
        comandaStationId: job.comandaStationId,
        printerName: printer.name,
        createdAt: new Date(),
        status: 'printed',
        data: [...job.data],
        title: job.title,
        subtitle: job.subtitle,
        metadata: job.metadata,
      });
    } catch (e) {
      store.addPrintJob({
        printerType: job.printerType,
        comandaStationId: job.comandaStationId,
        printerName: printer.name,
        createdAt: new Date(),
        status: jobStatusOfError(e),
        errorMessage: e instanceof Error ? e.message : String(e),
        retrySafety: retrySafetyOf(e),
        data: [...job.data],
        title: job.title,
        subtitle: job.subtitle,
        metadata: job.metadata,
      });
      throw e;
    } finally {
      busyJobIds.value = busyJobIds.value.filter((id) => id !== job.jobId);
    }
  }

  const anyBusy = computed(() => busyJobIds.value.length > 0);

  return {
    busyJobIds,
    anyBusy,
    canRetry,
    canReprint,
    needsReprintConfirm,
    resubmitTargetOf,
    resubmit,
  };
}
